const DEFAULT_MAX_WIDTH = 768;
const DEFAULT_MAX_HEIGHT = 1024;
const DEFAULT_JPEG_QUALITY = 0.6;
const MAX_BASE64_CHARS = 30_000;
const MIN_QUALITY = 0.3;

interface IScreenshotSuccess {
  __screenshot: true;
  base64: string;
  mediaType: "image/jpeg";
  description: string;
  metadata: {
    width: number;
    height: number;
    kuikaId: string | null;
  };
}

interface IScreenshotError {
  error: string;
}

/**
 * Inject a style sheet into the iframe that hides all design-time overlays
 * (selection wrappers, hover wrappers, inspector layer, drag placeholders,
 * dot-grid pseudo-elements, and padding boundary helpers).
 * Returns a cleanup function that removes the injected style.
 */
function hideDesignOverlays(iframeDoc: Document): () => void {
  const style = iframeDoc.createElement("style");
  style.setAttribute("data-screenshot-cleanup", "true");
  style.textContent = `
    #kuika_hoverWrapper,
    #kuika_selectedWrapper,
    #kuika_dragDropPlaceholder,
    .mcp-chat-inspector-layer,
    .kuika_padding_boundary_overlay {
      display: none !important;
    }
    #rootDI::before,
    #rootDI::after {
      display: none !important;
    }
  `;
  iframeDoc.head.appendChild(style);

  return () => {
    style.remove();
  };
}

export async function captureDesignerScreenshot(input: {
  kuikaId?: string;
  quality?: number;
  maxWidth?: number;
}): Promise<IScreenshotSuccess | IScreenshotError> {
  const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement | null;
  if (!iframe) {
    return { error: "Designer canvas iframe not found (kuika_iframeAgent_canvas)" };
  }

  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!iframeDoc) {
    return { error: "Cannot access iframe document — cross-origin restriction or iframe not loaded" };
  }

  let targetElement: HTMLElement | null = null;
  let description = "Full screen capture";

  if (input.kuikaId) {
    targetElement = iframeDoc.querySelector(`[kuikaid="${input.kuikaId}"]`) as HTMLElement | null;
    if (!targetElement) {
      return { error: `Component with kuikaId "${input.kuikaId}" not found in the rendered screen` };
    }
    description = `Component capture (kuikaId: ${input.kuikaId})`;
  } else {
    targetElement = iframeDoc.getElementById("rootDI") || iframeDoc.body;
    if (!targetElement) {
      return { error: "No renderable content found in the designer canvas" };
    }
  }

  // Hide design-time overlays before capture
  const restoreOverlays = hideDesignOverlays(iframeDoc);

  const overrideTargets: { el: HTMLElement; prev: string }[] = [];
  for (const element of [targetElement, iframeDoc.body, iframeDoc.documentElement]) {
    if (!element) continue;
    const style = iframeDoc.defaultView?.getComputedStyle(element);
    if (style && (style.overflow !== "visible" || style.overflowY !== "visible")) {
      overrideTargets.push({ el: element, prev: element.style.overflow });
      element.style.overflow = "visible";
    }
  }

  const html2canvas = (await import("html2canvas")).default;

  let canvas: HTMLCanvasElement;
  try {
    canvas = await html2canvas(targetElement, {
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      logging: false,
      width: targetElement.scrollWidth,
      height: targetElement.scrollHeight,
      windowWidth: targetElement.scrollWidth,
      windowHeight: targetElement.scrollHeight,
      scrollX: 0,
      scrollY: 0
    });
  } finally {
    for (const { el, prev } of overrideTargets) {
      el.style.overflow = prev;
    }
    restoreOverlays();
  }

  const effectiveMaxWidth = Math.min(Math.max(input.maxWidth || DEFAULT_MAX_WIDTH, 256), 2048);
  const effectiveMaxHeight = DEFAULT_MAX_HEIGHT;
  const effectiveQuality = Math.min(Math.max(input.quality || DEFAULT_JPEG_QUALITY, 0.1), 1.0);

  let finalCanvas: HTMLCanvasElement = canvas;
  if (canvas.width > effectiveMaxWidth) {
    const scale = effectiveMaxWidth / canvas.width;
    const newWidth = effectiveMaxWidth;
    const newHeight = Math.round(canvas.height * scale);

    const offscreen = document.createElement("canvas");
    offscreen.width = newWidth;
    offscreen.height = newHeight;
    const context = offscreen.getContext("2d");
    if (context) {
      context.drawImage(canvas, 0, 0, newWidth, newHeight);
      finalCanvas = offscreen;
    }
  }

  if (finalCanvas.height > effectiveMaxHeight) {
    const scale = effectiveMaxHeight / finalCanvas.height;
    const newWidth = Math.round(finalCanvas.width * scale);
    const newHeight = effectiveMaxHeight;
    const offscreen = document.createElement("canvas");
    offscreen.width = newWidth;
    offscreen.height = newHeight;
    const ctx = offscreen.getContext("2d");
    if (ctx) {
      ctx.drawImage(finalCanvas, 0, 0, newWidth, newHeight);
      finalCanvas = offscreen;
    }
  }

  let currentQuality = effectiveQuality;
  let base64: string;
  do {
    const dataUrl = finalCanvas.toDataURL("image/jpeg", currentQuality);
    base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, "");
    if (base64.length <= MAX_BASE64_CHARS || currentQuality <= MIN_QUALITY) break;
    currentQuality = Math.max(currentQuality - 0.1, MIN_QUALITY);
  } while (true);

  return {
    __screenshot: true,
    base64,
    mediaType: "image/jpeg",
    description,
    metadata: {
      width: finalCanvas.width,
      height: finalCanvas.height,
      kuikaId: input.kuikaId || null
    }
  };
}
